"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from "@/app/i18n/client";
import { useEffect } from "react";
import { AddressFields, AddressWithLocation } from "@/types/location";
import SearchField, { SearchProps } from "@/utils/SearchField";
import { autocompleteAddress, AutocompletePrediction } from "@/utils/placesApiCalls";
import { PlacesAddressType } from "@/types/google/placesApi";
type AddressBlockProps = {
  address: AddressWithLocation;
  disabled?: boolean;
} & ({
  useSearchField?: false;
} | {
  useSearchField: true;
  id: string;
  googlemapsLib: {
    loaded: boolean;
    error?: Error;
    load: () => void;
  };
  onLocationChanged: (addr: AddressWithLocation) => void;
});
type FormGroupBlockProps = {
  name: keyof Omit<AddressFields, "state" | "line2">;
  label: string;
  value: string;
  useSearchField?: boolean;
};
const autocompleteTypesForFields = {
  street: "street_address",
  city: "locality",
  number: "street_number",
  postal_code: "postal_code",
  state: "administrative_area_level_1",
  country: "country"
} as const satisfies Record<string, PlacesAddressType>;
export default function AddressBlock(t0) {
  const $ = _c(37);
  let address;
  let disabled;
  let props;
  if ($[0] !== t0) {
    ({
      disabled,
      address,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = address;
    $[2] = disabled;
    $[3] = props;
  } else {
    address = $[1];
    disabled = $[2];
    props = $[3];
  }
  const {
    i18n
  } = useTranslation();
  let t1;
  if ($[4] !== props.googlemapsLib || $[5] !== props.useSearchField) {
    t1 = () => {
      if (props.useSearchField) {
        props.googlemapsLib.load();
      }
    };
    $[4] = props.googlemapsLib;
    $[5] = props.useSearchField;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== disabled || $[9] !== i18n || $[10] !== props) {
    t3 = function FormGroupBlock(t4) {
      const {
        label,
        value,
        name
      } = t4;
      return <div className={`formGroupBlock ${disabled ? "disabled" : ""}`}><label htmlFor={name}>{label}</label>{props.useSearchField && (name === "street" || name === "number") ? <SearchField inputId={props.id + "-" + name} initialValue={value} inputOnSelect="value" disabled={disabled} httpFetchMethod={(searchTerm, abortSignal) => new Promise((resolve, reject) => {
          if (abortSignal?.aborted) {
            reject(new Error("Aborted"));
          }
          abortSignal?.addEventListener("abort", () => {
            reject(new Error("Aborted"));
          });
          if (!props.googlemapsLib.loaded) {
            props.googlemapsLib.load();
            setTimeout(() => {
              autocompleteAddress(searchTerm, i18n.language, [autocompleteTypesForFields[name]]).then(res => {
                const actualRes = res.map(_temp);
                resolve(actualRes);
              });
            }, 3000);
          } else {
            if (props.googlemapsLib.error) {
              return props.googlemapsLib.error;
            } else {
              autocompleteAddress(searchTerm, i18n.language, [autocompleteTypesForFields[name]]).then(res_0 => {
                resolve(res_0.map(_temp2));
              });
            }
          }
        })} resultGenerator={_temp3} maxQueryResults={3} delayQuery={150} onSelect={async addrPred => props.onLocationChanged(await addrPred.fillPlace())} /> : <input name={name} disabled={disabled || props.useSearchField} value={value} readOnly={disabled || props.useSearchField} />}</div>;
    };
    $[8] = disabled;
    $[9] = i18n;
    $[10] = props;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const FormGroupBlock = t3;
  let t4;
  if ($[12] !== FormGroupBlock || $[13] !== address.address_fields.street) {
    t4 = <FormGroupBlock name="street" label="Street" value={address.address_fields.street} />;
    $[12] = FormGroupBlock;
    $[13] = address.address_fields.street;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== FormGroupBlock || $[16] !== address.address_fields.number) {
    t5 = <FormGroupBlock name="number" label="Number" value={address.address_fields.number} />;
    $[15] = FormGroupBlock;
    $[16] = address.address_fields.number;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== FormGroupBlock || $[22] !== address.address_fields.city) {
    t7 = <FormGroupBlock name="city" label="City" value={address.address_fields.city} />;
    $[21] = FormGroupBlock;
    $[22] = address.address_fields.city;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== FormGroupBlock || $[25] !== address.address_fields.postal_code) {
    t8 = <FormGroupBlock name="postal_code" label="Zip" value={address.address_fields.postal_code} />;
    $[24] = FormGroupBlock;
    $[25] = address.address_fields.postal_code;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div>{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== FormGroupBlock || $[31] !== address.address_fields.country) {
    t10 = <div><FormGroupBlock name="country" label="Country" value={address.address_fields.country} /></div>;
    $[30] = FormGroupBlock;
    $[31] = address.address_fields.country;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t6 || $[35] !== t9) {
    t11 = <div className="address-block">{t6}{t9}{t10}</div>;
    $[33] = t10;
    $[34] = t6;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp3(searchResult) {
  return <div>{searchResult.formattedAddress}</div>;
}
function _temp2(addrWL_0, i_0) {
  const WithSearchTerm_0 = {
    ...addrWL_0,
    id: i_0,
    score: i_0,
    value: addrWL_0.formattedAddress
  };
  return WithSearchTerm_0;
}
function _temp(addrWL, i) {
  const WithSearchTerm = {
    ...addrWL,
    id: i,
    score: i,
    value: addrWL.formattedAddress
  };
  return WithSearchTerm;
}